import React, { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import {
  Send, Bot, User, Copy, Check, RotateCcw,
  Loader2, Coins, Clock
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { CLOUDMIND_MODELS, streamCompletion } from '@/lib/api'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'
import { formatTokens, formatCurrency } from '@/lib/currency'
import type { CompletionMessage } from '@/lib/api'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: (string | undefined | null | boolean)[]) {
  return twMerge(clsx(inputs))
}

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tokens?: number
  costUSD?: number
  isStreaming?: boolean
}

const DEFAULT_SYSTEM_PROMPT = 'You are a helpful AI assistant. Be concise and accurate.'

export default function PlaygroundPage() {
  const [searchParams] = useSearchParams()
  const { getToken } = useAuth()
  const initialModel = searchParams.get('model') || 'cm-speed'

  const [selectedModel, setSelectedModel] = useState(initialModel)
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [totalUsage, setTotalUsage] = useState({ tokens: 0, cost: 0 })

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('currency_preference').single()
      return data
    },
  })
  const currency = customer?.currency_preference || 'PHP'

  const model = CLOUDMIND_MODELS.find(m => m.id === selectedModel)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (ta) {
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, 200) + 'px'
    }
  }, [input])

  const handleCopy = (content: string, id: string) => {
    navigator.clipboard.writeText(content)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return
    if (!apiKey.trim()) {
      alert('Please enter an API key to use the playground.')
      return
    }

    const userMessage: Message = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: input.trim(),
    }

    const assistantId = `msg_${Date.now() + 1}`
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      isStreaming: true,
    }

    setMessages(prev => [...prev, userMessage, assistantMessage])
    setInput('')
    setIsStreaming(true)

    const conversationMessages: CompletionMessage[] = [
      { role: 'system', content: systemPrompt },
      ...messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
      { role: 'user', content: userMessage.content },
    ]

    try {
      await streamCompletion(
        selectedModel,
        conversationMessages,
        apiKey,
        (chunk) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId ? { ...m, content: m.content + chunk } : m
            )
          )
        },
        (usage) => {
          const costUSD =
            (usage.prompt_tokens * (model?.input_cost_per_1m ?? 0.1)) / 1_000_000 +
            (usage.completion_tokens * (model?.output_cost_per_1m ?? 0.2)) / 1_000_000

          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, isStreaming: false, tokens: usage.completion_tokens, costUSD }
                : m
            )
          )
          setTotalUsage(prev => ({
            tokens: prev.tokens + usage.total_tokens,
            cost: prev.cost + costUSD,
          }))
          setIsStreaming(false)
        }
      )
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error'
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: `Error: ${errMsg}`, isStreaming: false }
            : m
        )
      )
      setIsStreaming(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col gap-4 animate-fade-in">
      {/* Config bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label className="text-sm font-medium text-gray-600 shrink-0">Model:</label>
          <div className="w-52">
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLOUDMIND_MODELS.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center gap-2">
                      <span>{m.name}</span>
                      <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded-full font-medium',
                        m.tier === 'speed' ? 'bg-green-100 text-green-700' :
                        m.tier === 'standard' ? 'bg-blue-100 text-blue-700' :
                        m.tier === 'premium' ? 'bg-purple-100 text-purple-700' :
                        'bg-orange-100 text-orange-700'
                      )}>
                        {m.tier}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600 shrink-0">API Key:</label>
          <input
            type="password"
            placeholder="cm_sg_live_..."
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            className="w-48 h-9 px-3 text-xs rounded-lg border border-gray-300 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {totalUsage.tokens > 0 && (
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Coins className="h-3.5 w-3.5" />
              {formatTokens(totalUsage.tokens)} tokens
            </span>
            <span className="flex items-center gap-1">
              Cost: {formatCurrency(totalUsage.cost, currency)}
            </span>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setMessages([]); setTotalUsage({ tokens: 0, cost: 0 }) }}
          className="ml-auto"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Clear
        </Button>
      </div>

      {/* System prompt */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">System Prompt</label>
        <textarea
          value={systemPrompt}
          onChange={e => setSystemPrompt(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
        />
      </div>

      {/* Messages */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <Bot className="h-10 w-10 mb-3 text-gray-300" />
              <p className="font-medium text-gray-500">Start a conversation</p>
              <p className="text-sm mt-1">
                Select a model, enter your API key, and type a message below.
              </p>
              {model && (
                <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                  <Badge variant={model.tier as 'speed' | 'standard' | 'premium' | 'media'}>
                    {model.tier}
                  </Badge>
                  <span className="text-gray-400">
                    {(model.context_window / 1000).toFixed(0)}K ctx
                  </span>
                  {model.speed_ms && (
                    <span className="flex items-center gap-1 text-gray-400">
                      <Clock className="h-3 w-3" />
                      ~{model.speed_ms}ms
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            messages.map(msg => (
              <div
                key={msg.id}
                className={cn(
                  'flex gap-3',
                  msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  'h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5',
                  msg.role === 'user' ? 'bg-indigo-100' : 'bg-gray-100'
                )}>
                  {msg.role === 'user' ? (
                    <User className="h-4 w-4 text-indigo-600" />
                  ) : (
                    <Bot className="h-4 w-4 text-gray-600" />
                  )}
                </div>

                {/* Bubble */}
                <div className={cn(
                  'max-w-[75%] rounded-2xl px-4 py-2.5 text-sm relative group',
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                )}>
                  <div className="whitespace-pre-wrap break-words">
                    {msg.content}
                    {msg.isStreaming && (
                      <span className="inline-block ml-1 h-4 w-0.5 bg-gray-500 animate-pulse" />
                    )}
                  </div>

                  {/* Message metadata */}
                  {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
                    <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-gray-200">
                      {msg.tokens && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Coins className="h-3 w-3" />
                          {msg.tokens} tokens
                        </span>
                      )}
                      {msg.costUSD && (
                        <span className="text-xs text-gray-400">
                          {formatCurrency(msg.costUSD, currency)}
                        </span>
                      )}
                      <button
                        onClick={() => handleCopy(msg.content, msg.id)}
                        className="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {copiedId === msg.id ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message... (Enter to send, Shift+Enter for newline)"
              rows={1}
              className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[42px]"
              disabled={isStreaming}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isStreaming || !apiKey.trim()}
              className="h-10 w-10 p-0 rounded-xl shrink-0"
            >
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
